import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Alert, Row, Col } from "react-bootstrap";
import Select from "react-select";
import AddressAutocomplete from "./AddressAutocomplete";
import api from "../api";
import { useUser } from "../contextProviders/userContext";
import { FaWhatsapp } from "react-icons/fa";

const PROJECT_DRAFT_KEY_PREFIX = "wcco:create-project-draft";
const INITIAL_PROJECT_DATA = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
  organiserType: "Individual",
  organiserName: "",
  categories: [],
  sdgs: [],
  locationType: "Online",
  location: {
    address: "",
    coordinates: {
      lat: null,
      lng: null,
    },
    transportProvided: false,
    transportDetails: "",
  },
  weeklyHoursRequired: {
    minimum: 0,
    maximum: 0,
  },
  participantTypes: [
    {
      role: "",
      additionalRequirements: "",
    },
  ],
  communicationLinks: {
    whatsappGroup: "",
    whatsappChannel: "",
  },
  visibility: "public",
};

const loadProjectDraft = (draftKey) => {
  if (!draftKey) return INITIAL_PROJECT_DATA;

  try {
    const savedDraft = localStorage.getItem(draftKey);
    return savedDraft ? JSON.parse(savedDraft) : INITIAL_PROJECT_DATA;
  } catch (error) {
    localStorage.removeItem(draftKey);
    return INITIAL_PROJECT_DATA;
  }
};

const CreateProjectModal = ({
  show,
  onHide,
  onProjectCreated,
  interests,
  sdgOptions,
}) => {
  const { user } = useUser();
  const draftKey = user?.id
    ? `${PROJECT_DRAFT_KEY_PREFIX}:${user.id}`
    : null;
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMessage("");
      setLoading(false);
      setIsClosing(false);
    }
  }, [show]);

  const handleClose = () => {
    if (successMessage || isClosing) {
      onHide();
      setProjectData(INITIAL_PROJECT_DATA);
    } else {
      onHide();
    }
  };

  const [projectData, setProjectData] = useState(() =>
    loadProjectDraft(draftKey)
  );

  useEffect(() => {
    if (!draftKey) return;

    if (JSON.stringify(projectData) === JSON.stringify(INITIAL_PROJECT_DATA)) {
      localStorage.removeItem(draftKey);
    } else {
      localStorage.setItem(draftKey, JSON.stringify(projectData));
    }
  }, [draftKey, projectData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setProjectData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setProjectData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleMultiSelect = (field, selectedOptions) => {
    setProjectData((prev) => ({
      ...prev,
      [field]: selectedOptions.map((option) => option.value),
    }));
  };

  const handleLocationSelect = (locationData) => {
    setProjectData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        address: locationData.address,
        coordinates: {
          lat: locationData.coordinates.lat,
          lng: locationData.coordinates.lng,
        },
      },
    }));
  };

  const handleParticipantTypeChange = (index, field, value) => {
    setProjectData((prev) => ({
      ...prev,
      participantTypes: prev.participantTypes.map((type, i) =>
        i === index ? { ...type, [field]: value } : type
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    // Validate weekly hours
    if (
      projectData.weeklyHoursRequired.maximum <
      projectData.weeklyHoursRequired.minimum
    ) {
      setError("Maximum weekly hours cannot be less than minimum weekly hours");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/projects", projectData);

      if (draftKey) localStorage.removeItem(draftKey);
      setSuccessMessage("Project created successfully! Waiting for approval.");
      onProjectCreated(response.data);
      setIsClosing(true);

      // Delay closing the modal to ensure the success message is visible
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error creating project:", error);
      setError(
        error.authSessionExpired
          ? "Your session expired and could not be renewed. Please sign in again; your project draft has been saved."
          : error.response?.data?.error ||
          "Failed to create project. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }}>
        <Modal.Title style={{ color: "var(--color-primary)", fontWeight: "600" }}>Create New Project</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body-scroll p-4">
        {successMessage && (
          <Alert variant="success" className="mb-3 fade show">
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <h5 className="mb-3 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Basic Information</h5>
          <Form.Group className="mb-3">
            <Form.Label>Project Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={projectData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter project name"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={projectData.description}
              onChange={handleInputChange}
              required
              placeholder="Describe your project"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={projectData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={projectData.endDate}
                  onChange={handleInputChange}
                  min={projectData.startDate}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Categories and SDGs Section */}
          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Categories and SDGs</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Categories</Form.Label>
                <Select
                  isMulti
                  name="categories"
                  options={interests.map((cat) => ({ value: cat, label: cat }))}
                  value={projectData.categories.map((cat) => ({
                    value: cat,
                    label: cat,
                  }))}
                  onChange={(selected) =>
                    handleMultiSelect("categories", selected || [])
                  }
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select categories"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sustainable Development Goals (SDGs)</Form.Label>
                <Select
                  isMulti
                  name="sdgs"
                  options={sdgOptions.map((sdg) => ({
                    value: sdg,
                    label: sdg,
                  }))}
                  value={projectData.sdgs.map((sdg) => ({
                    value: sdg,
                    label: sdg,
                  }))}
                  onChange={(selected) =>
                    handleMultiSelect("sdgs", selected || [])
                  }
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select SDGs"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Organization Section */}
          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Organization Details</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organiser Type</Form.Label>
                <Form.Select
                  name="organiserType"
                  value={projectData.organiserType}
                  onChange={handleInputChange}
                  required>
                  <option value="Individual">Individual</option>
                  <option value="Student Group">Student Group</option>
                  <option value="Club or Society">Club or Society</option>
                  <option value="House Committee">House Committee</option>
                  <option value="Sports Club">Sports Club</option>
                  <option value="Student Council">Student Council</option>
                  <option value="External Organisation">External Organisation</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organiser Name</Form.Label>
                <Form.Control
                  type="text"
                  name="organiserName"
                  value={projectData.organiserName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter organizer name"
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Communication links</h5>
          <p className="text-muted small">Optional official WhatsApp links students can use for updates and coordination.</p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaWhatsapp className="me-2" aria-hidden="true" />WhatsApp group link</Form.Label>
                <Form.Control
                  type="url"
                  name="communicationLinks.whatsappGroup"
                  value={projectData.communicationLinks?.whatsappGroup || ""}
                  onChange={handleInputChange}
                  placeholder="https://chat.whatsapp.com/..."
                />
                <Form.Text>Use an official WhatsApp group invitation link.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><FaWhatsapp className="me-2" aria-hidden="true" />WhatsApp channel link</Form.Label>
                <Form.Control
                  type="url"
                  name="communicationLinks.whatsappChannel"
                  value={projectData.communicationLinks?.whatsappChannel || ""}
                  onChange={handleInputChange}
                  placeholder="https://whatsapp.com/channel/..."
                />
                <Form.Text>Use an official WhatsApp channel link.</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Location Section */}
          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Location Details</h5>
          <Form.Group className="mb-3">
            <Form.Label>Location Type</Form.Label>
            <Form.Select
              name="locationType"
              value={projectData.locationType}
              onChange={handleInputChange}
              required>
              <option value="Online">Online</option>
              <option value="In-Person">In-Person</option>
              <option value="Hybrid">Hybrid</option>
            </Form.Select>
          </Form.Group>

          {(projectData.locationType === "In-Person" ||
            projectData.locationType === "Hybrid") && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Location Address <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <AddressAutocomplete onSelect={handleLocationSelect} />
                {projectData.location.address && (
                  <Form.Text className="text-muted">
                    Selected: {projectData.location.address}
                    <br />
                    Coordinates: {projectData.location.coordinates.lat},{" "}
                    {projectData.location.coordinates.lng}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Transport Provided"
                  name="transportProvided"
                  checked={projectData.location.transportProvided}
                  onChange={(e) => {
                    setProjectData((prev) => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        transportProvided: e.target.checked,
                      },
                    }));
                  }}
                />
              </Form.Group>

              {projectData.location.transportProvided && (
                <Form.Group className="mb-3">
                  <Form.Label>Transport Details</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="transportDetails"
                    value={projectData.location.transportDetails}
                    onChange={(e) => {
                      setProjectData((prev) => ({
                        ...prev,
                        location: {
                          ...prev.location,
                          transportDetails: e.target.value,
                        },
                      }));
                    }}
                    placeholder="Describe transport arrangements"
                  />
                </Form.Group>
              )}
            </>
          )}

          {/* Time Commitment Section */}
          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Time Commitment</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Minimum Weekly Hours</Form.Label>
                <Form.Control
                  type="number"
                  name="weeklyHoursRequired.minimum"
                  value={projectData.weeklyHoursRequired.minimum}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maximum Weekly Hours</Form.Label>
                <Form.Control
                  type="number"
                  name="weeklyHoursRequired.maximum"
                  value={projectData.weeklyHoursRequired.maximum}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Participant Requirements Section */}
          <h5 className="mb-3 mt-4 fw-bold" style={{ color: "var(--color-primary)", borderBottom: "2px solid var(--color-border)", paddingBottom: "6px" }}>Participant Requirements</h5>
          <Form.Group className="mb-3">
            <Form.Label>Role Description</Form.Label>
            <Form.Control
              as="textarea"
              value={projectData.participantTypes[0].role}
              onChange={(e) =>
                handleParticipantTypeChange(0, "role", e.target.value)
              }
              placeholder="Describe the role"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Project Visibility</Form.Label>
                <Form.Select
                  name="visibility"
                  value={projectData.visibility}
                  onChange={handleInputChange}
                  required>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Additional Requirements</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={projectData.participantTypes[0].additionalRequirements}
              onChange={(e) =>
                handleParticipantTypeChange(
                  0,
                  "additionalRequirements",
                  e.target.value
                )
              }
              placeholder="degree specific, skills specific, language specific, etc."
            />
          </Form.Group>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || isClosing}
            className="w-100 mt-4 btn-wcco-primary border-0"
            style={{ padding: "12px", borderRadius: "var(--radius-md)" }}>
            {loading
              ? "Creating..."
              : isClosing
              ? "Created!"
              : "Create Project"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateProjectModal;
